import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
// import { useNavigate } from 'react-router-dom';
import {
  Flex,
  Text,
  Input,
  FormControl,
  FormLabel,
  FormErrorMessage,
  Button,
  Select,
  Tooltip,
  VStack,
  Image,
  HStack,
  Textarea,
} from '@chakra-ui/react';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import api from "api/axiosConfig";
import API_BASE from "configs/hostConfig";
import defaultAvatar from 'assets/user.png';
import { GRADE_LEVELS, US_STATES, COUNTRIES } from 'utils/data/options';

const validationSchema = Yup.object().shape({
  username: Yup.string()
    .matches(
      /^[a-zA-Z][a-zA-Z0-9_]*$/,
      'Username must start with a letter and can only contain letters, numbers, and underscores'
    )
    .min(3, 'Username must be at least 3 characters long')
    .max(20, 'Username must be at most 20 characters long')
    .required('Username is required'),
  grade: Yup.string().required('Grade is required'),
  age: Yup.number()
    .min(6, 'Age must be at least 6')
    .max(120, 'Age must be at most 120'),
});

function ProfileForm({ profiles }) {
  // const navigate = useNavigate();
  const [avatar, setAvatar] = useState('');
  const [usernameFixed, setUsernameFixed] = useState(false);
  const [available, setAvailable] = useState(true);
  const [availableMessage, setAvailableMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [initialValues, setInitialValues] = useState({});

  useEffect(() => {
    setInitialValues(profiles);
    if (profiles?.avatarUrl) setAvatar(profiles?.avatarUrl);
    if (profiles?.username) setUsernameFixed(true);
    setAvailableMessage('');
  }, [profiles]);

  const handleAvatarPreview = (url) => {
    if (url) setAvatar(url);
  };

  function checkUsernameAvailability(name) {
    setIsSubmitting(true);
    api
      .get(`${API_BASE}/api/auth/check-username?username=${name}`)
      .then((res) => {
        if (res.status == 200) {
          const exists = res.data?.data;
          if (exists) {
            setAvailable(false);
            setAvailableMessage('Sorry, the username is taken');
          } else {
            setAvailable(true);
            setAvailableMessage("The username is available—you're good to go!");
          }
        }
      })
      .catch(() => console.log('Failed to update profiles!'))
      .finally(() => setIsSubmitting(false));
  }

  function updateProfile(values) {
    // console.log('Form submitted 2222:', values);
    setIsSubmitting(true);
    const body = {
      username: values.username,
      avatarUrl: values.avatarUrl,
      grade: values.grade,
      age: values.age,
      // sex: values.sex,
      state: values.state,
      country: values.country,
      bio: values.bio,
      // mobilePhone: '',
      // dob: generateDob(values.dob),
      // emailSubscription: values.emailSubscription,
    };
    api
      .post(`${API_BASE}/api/user/profile`, body)
      .then((res) => {
        if (res.status == 200) {
          const username = res?.data;
          if (username) {
            setUsernameFixed(true);
          }
          // navigate('/student-profile/dashboard');
        }
      })
      .catch(() => console.log('Failed to update profiles!'))
      .finally(() => setIsSubmitting(false));
  }

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      enableReinitialize
    >
      {({ values }) => (
        <Form>
          <VStack spacing={6} align="stretch">
            {/* Avatar Image */}
            <Flex justify="center" align="center">
              <Image
                src={avatar || defaultAvatar}
                boxSize="120px"
                borderRadius="full"
              />
            </Flex>

            {/* Avatar URL with Preview */}
            <HStack spacing={2}>
              <Field name="avatarUrl">
                {({ field }) => (
                  <FormControl>
                    <FormLabel>Avatar URL</FormLabel>
                    <Input {...field} placeholder="Enter avatar URL" />
                  </FormControl>
                )}
              </Field>
              <Button
                mt="32px"
                onClick={() => handleAvatarPreview(values.avatarUrl)}
              >
                Preview
              </Button>
            </HStack>

            {/* Bio */}
            <FormControl>
              <FormLabel>Bio</FormLabel>
              <Field
                name="bio"
                as={Textarea}
                placeholder="Tell us about yourself..."
              />
            </FormControl>

            {/* username */}
            <HStack spacing={2}>
              <Field name="username">
                {({ field, form }) => (
                  <FormControl
                    isInvalid={form.errors.username && form.touched.username}
                    isRequired
                  >
                    <FormLabel>Username (can&apos;t be changed)</FormLabel>
                    <Input
                      {...field}
                      placeholder="Enter your username"
                      isDisabled={usernameFixed}
                      onChange={(e) => {
                        field.onChange(e);
                        setAvailableMessage('');
                      }}
                    />
                    <FormErrorMessage>{form.errors.username}</FormErrorMessage>
                  </FormControl>
                )}
              </Field>
              <Button
                mt="32px"
                minW="160px"
                onClick={() => checkUsernameAvailability(values.username)}
                isDisabled={usernameFixed}
                isLoading={isSubmitting}
              >
                Check Availability
              </Button>
            </HStack>
            {availableMessage?.length > 0 && (
              <Text color={available ? 'green.500' : 'red.500'}>
                {availableMessage}
              </Text>
            )}
            {/* Grade, Age */}
            <HStack spacing={4}>
              <Field name="grade">
                {({ field, form }) => (
                  <FormControl
                    isInvalid={form.errors.grade && form.touched.grade}
                    isRequired
                  >
                    <FormLabel>Grade</FormLabel>
                    <Select {...field} placeholder="Select grade">
                      {GRADE_LEVELS.map((g) => (
                        <option key={g.id} value={g.id}>
                          {g.name}
                        </option>
                      ))}
                    </Select>
                    <FormErrorMessage>{form.errors.grade}</FormErrorMessage>
                  </FormControl>
                )}
              </Field>
              <Field name="age">
                {({ field, form }) => (
                  <FormControl isInvalid={form.errors.age && form.touched.age}>
                    <FormLabel>Age</FormLabel>
                    <Input {...field} />
                    <FormErrorMessage>{form.errors.age}</FormErrorMessage>
                  </FormControl>
                )}
              </Field>
            </HStack>

            {/* Country and State */}
            <HStack spacing={4}>
              <Field name="country">
                {({ field }) => (
                  <FormControl>
                    <Tooltip label="Optional but recommended for personalized services">
                      <FormLabel>Country</FormLabel>
                    </Tooltip>
                    <Select {...field} placeholder="Select country">
                      {COUNTRIES.map((c) => (
                        <option key={c.abbr} value={c.abbr}>
                          {c.name}
                        </option>
                      ))}
                    </Select>
                  </FormControl>
                )}
              </Field>

              <Field name="state">
                {({ field }) => (
                  <FormControl>
                    <Tooltip label="Optional but recommended for personalized services">
                      <FormLabel>State</FormLabel>
                    </Tooltip>
                    <Select {...field} placeholder="Select state">
                      {US_STATES.map((s) => (
                        <option key={s.abbr} value={s.abbr}>
                          {s.name}
                        </option>
                      ))}
                    </Select>
                  </FormControl>
                )}
              </Field>
            </HStack>

            {/* Submit Button */}
            <Button
              type="button"
              colorScheme="green"
              alignSelf="flex-start"
              disabled={isSubmitting}
              onClick={() => updateProfile(values)}
            >
              Update Profile
            </Button>
          </VStack>
        </Form>
      )}
    </Formik>
  );
}

ProfileForm.propTypes = {
  profiles: PropTypes.objectOf(
    PropTypes.oneOfType([PropTypes.string, PropTypes.number])
  ),
};

export default ProfileForm;
